import React, { useEffect } from 'react';

import SEO from '../SEO';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, Wrench, Settings, Clock, Award, FileCheck, Users } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const ReliableRepairMethods = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const repairMethods = [
    {
      title: 'Cold Bonding Technology',
      description: 'Room-temperature curing repairs that eliminate hot work hazards and can be performed in live plant conditions.',
      advantages: ['No fire risk', 'No welding permits required', 'Minimal production disruption', 'Performed in-situ']
    },
    {
      title: 'Metal Rebuilding',
      description: 'Restore worn equipment to original specifications using engineered composite materials.',
      advantages: ['Restore tolerances', 'Rebuild worn surfaces', 'Fill cavities and voids', 'Machine to finish']
    },
    {
      title: 'Leak Sealing',
      description: 'Fast, permanent solutions for pipes, tanks, and equipment without shutdown.',
      advantages: ['Stop active leaks', 'Underwater applications', 'High-pressure systems', 'Chemical resistant']
    },
    {
      title: 'Protective Lining',
      description: 'Long-lasting protection against corrosion, erosion, and chemical attack.',
      advantages: ['Extend service life', 'Resist abrasion', 'Chemical barriers', 'Easy maintenance']
    }
  ];

  const qualityStandards = [
    { icon: FileCheck, title: 'Documented Procedures', description: 'Every repair follows certified application procedures' },
    { icon: Users, title: 'Trained Technicians', description: 'Factory-certified applicators with ongoing training' },
    { icon: Award, title: 'Quality Materials', description: 'Only premium, proven repair materials used' },
    { icon: Clock, title: 'Warranty Backed', description: 'Extended warranties on all repair work' }
  ];

  const successMetrics = [
    { value: '99%', label: 'First-Time Fix Rate' },
    { value: '15+', label: 'Years Average Repair Life' },
    { value: '24hr', label: 'Emergency Response' },
    { value: '100%', label: 'Safety Record' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="Reliable Repair Methods" description="Proven, reliable repair methods for industrial equipment and infrastructure using epoxy systems." path="/maintenance/reliable-repair-methods" />
      
      <PageHero
        badge="SMART Program"
        title="Reliable Repair Methods"
        description="Proven techniques backed by decades of industrial experience and field success. Our repair methods are engineered for reliability and long-term performance."
        backgroundKey="reliable-repair-methods"
        buttonText="Discuss Your Repair Needs"
      />

      {/* Repair Methods */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Repair Methods</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {repairMethods.map((method, index) => (
              <Card key={index} className="overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-green-700 to-green-800 text-white">
                  <CardTitle className="flex items-center">
                    <Wrench className="mr-3" size={24} />
                    {method.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-gray-600 mb-4">{method.description}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {method.advantages.map((adv, idx) => (
                      <div key={idx} className="flex items-center text-sm">
                        <CheckCircle className="text-green-700 mr-2 flex-shrink-0" size={16} />
                        <span className="text-gray-700">{adv}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Quality Assurance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualityStandards.map((standard, index) => {
              const IconComponent = standard.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm text-center">
                  <IconComponent className="mx-auto text-green-700 mb-4" size={40} />
                  <h3 className="font-bold text-slate-900 mb-2">{standard.title}</h3>
                  <p className="text-gray-600 text-sm">{standard.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-green-700 text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">Our Track Record</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {successMetrics.map((metric, index) => (
              <div key={index}>
                <div className="text-4xl font-bold mb-2">{metric.value}</div>
                <div className="text-green-100">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Settings className="mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold mb-4">Ready for Reliable Repairs?</h2>
          <p className="text-blue-100 mb-8">
            Let our experienced team assess your equipment and recommend the optimal repair solution.
          </p>
          <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/#contact')}>
            Get a Repair Quote <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReliableRepairMethods;
